'use client'
import 'client-only'
import { getUsableZipCode } from '@/shared/utilities/zip-code.utility'

/**
 * Utility for storing the user's zip code in sessionStorage during their browser session.
 * Persists across page navigations within the same tab; cleared when the tab closes.
 */

const SESSION_ZIP_KEY = 'hubhive_session_zip'
const SESSION_ZIP_GEOIP_ATTEMPTED_KEY = 'hubhive_session_zip_geoip_attempted'

export type SessionZipData = {
  zipCode: string
}

export function getSessionZip(): string {
  try {
    const stored = sessionStorage.getItem(SESSION_ZIP_KEY)
    if (!stored) return ''

    const data = JSON.parse(stored) as SessionZipData
    return getUsableZipCode(data.zipCode)
  } catch (error) {
    console.error('Failed to retrieve session zip:', error)
    return ''
  }
}

export function setSessionZip(zip: string): void {
  try {
    const trimmed = getUsableZipCode(zip)
    if (trimmed) {
      sessionStorage.setItem(SESSION_ZIP_KEY, JSON.stringify({ zipCode: trimmed }))
    } else {
      sessionStorage.removeItem(SESSION_ZIP_KEY)
    }
  } catch (error) {
    console.error('Failed to store session zip:', error)
  }
}

/**
 * Records whether the IP-based geolocation lookup has already been attempted
 * this browser session. The lookup is a network round-trip and only ever
 * provides a fallback zip, so it must run at most once — even when it yields
 * nothing usable (e.g. a non-public IP resolving to the "00000" sentinel),
 * which would otherwise leave nothing persisted and re-fire on every page.
 */
export function hasAttemptedGeoIpZip(): boolean {
  try {
    return sessionStorage.getItem(SESSION_ZIP_GEOIP_ATTEMPTED_KEY) === '1'
  } catch {
    return false
  }
}

export function markGeoIpZipAttempted(): void {
  try {
    sessionStorage.setItem(SESSION_ZIP_GEOIP_ATTEMPTED_KEY, '1')
  } catch {
    // sessionStorage unavailable (private mode) — we just retry next mount.
  }
}

export function clearSessionZip(): void {
  try {
    sessionStorage.removeItem(SESSION_ZIP_KEY)
  } catch (error) {
    console.error('Failed to clear session zip:', error)
  }
}